import { useState, useEffect } from 'react';
import { articlesApi } from '../../lib/api';

/**
 * Fetches an article by slug. While loading, returns `loading=true`. On
 * success, populates `article`. On error (404 etc), falls back to fetching
 * keyword-based search results, then most-popular articles, for the
 * "article not found" page.
 *
 * Also tracks the view and records into "recently viewed" via `saveProgress`.
 */
export function useFetchArticle(slug, saveProgress) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestedArticles, setSuggestedArticles] = useState([]);
  const [currentSlug, setCurrentSlug] = useState(slug);

  // Synchronous render-time reset: prevents a one-tick stale article (old slug)
  // from leaking into the first render after client-side slug navigation.
  if (currentSlug !== slug) {
    setCurrentSlug(slug);
    setArticle(null);
    setLoading(true);
  }

  useEffect(() => {
    let cancelled = false;
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const response = await articlesApi.getBySlug(slug);
        if (cancelled) return;
        setArticle(response.data);
        articlesApi.trackView(slug).catch(() => {});
        const a = response.data;
        saveProgress(slug, {
          title: a.title,
          category: a.category,
          featured_image: a.featured_image,
          read_time: a.read_time,
          progress: 0,
        });
      } catch (error) {
        console.error('Failed to fetch article:', error);
        try {
          const keywords = slug.replace(/-/g, ' ').split(' ').filter((w) => w.length > 3).slice(0, 3).join(' ');
          let results = [];
          if (keywords) {
            const searchResp = await articlesApi.search(keywords);
            results = (searchResp.data || []).slice(0, 6);
          }
          if (results.length === 0) {
            const popResp = await articlesApi.getAll({ limit: 6, sort: '-view_count' });
            results = (popResp.data || []).slice(0, 6);
          }
          if (!cancelled) setSuggestedArticles(results);
        } catch { /* ignore */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    window.scrollTo(0, 0);
    fetchArticle();
    return () => { cancelled = true; };
  }, [slug, saveProgress]);

  return { article, loading, suggestedArticles };
}
